import {
  type FirstDayOfWeek,
  useLocalizationContext,
} from '@brightdrop/localization-core';
import {
  LocalizationProvider,
  type LocalizationProviderProps,
} from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import type { DateTime } from 'luxon';
import type { ReactNode } from 'react';

/** Luxon's weekday numbering, which is what the adapter has to speak. */
const WEEK_STARTS_ON: Readonly<Record<FirstDayOfWeek, number>> = {
  MONDAY: 1,
  SATURDAY: 6,
  SUNDAY: 7,
};

/**
 * An adapter that starts the week where the reader says, rather than where their locale assumes.
 *
 * v7 derives week start from the locale alone and offers no way to override it, so a reader whose
 * profile says Saturday still gets a Sunday calendar. Subclassing is the only seam: `startOfWeek`
 * is an instance property, so assigning it in the constructor replaces the one the base class set.
 *
 * Both ends are replaced, not just the start. `getWeekArray` measures the grid as the distance from
 * `startOfWeek(first of month)` to `endOfWeek(last of month)` and then cuts it into sevens, so an
 * end still derived from the locale leaves a row short whenever the two disagree.
 *
 * Without this the picker and every other week-aware surface would answer differently for the same
 * reader, which is the defect this work exists to close.
 */
const adapterStartingWeekOn = (firstDayOfWeek: FirstDayOfWeek) =>
  class extends AdapterLuxon {
    constructor(...args: ConstructorParameters<typeof AdapterLuxon>) {
      super(...args);

      // MUI picks `normalDateWithWeekday` for English and `normalDate` for every other locale, and
      // `normalDate` has no weekday, so a picker's toolbar date loses it outside English. The
      // pattern writes the day before the month, which holds for es-MX and fr-CA; a locale that
      // puts the month first would need its own format.
      this.formats = { ...this.formats, normalDate: 'ccc d MMM' };

      const startOfWeek = (value: DateTime): DateTime => {
        // `weekday` is ISO-numbered whatever the locale, so the arithmetic below owes it nothing;
        // the locale is carried along only because the rest of the adapter expects it on the value.
        const day = value.setLocale(this.locale).startOf('day');
        const back = (day.weekday - WEEK_STARTS_ON[firstDayOfWeek] + 7) % 7;

        return day.minus({ days: back });
      };

      this.startOfWeek = startOfWeek;
      this.endOfWeek = (value: DateTime): DateTime =>
        startOfWeek(value).plus({ days: 6 }).endOf('day');
    }
  };

/**
 * One class per week start, built once.
 *
 * MUI keys its picker localization off the adapter class, and remounts the pickers below it when
 * that identity changes. Three classes held for the life of the module means the identity depends
 * on the preference alone: a provider that remounts hands back the same class it did before, and
 * only a reader who actually changes which day their week starts on gets a different one.
 */
const ADAPTERS = {
  SUNDAY: adapterStartingWeekOn('SUNDAY'),
  MONDAY: adapterStartingWeekOn('MONDAY'),
  SATURDAY: adapterStartingWeekOn('SATURDAY'),
} as const satisfies Record<FirstDayOfWeek, unknown>;

/**
 * The one date adapter in the app, mounted below the gate so it can read the reader.
 *
 * Every picker inherits its locale and its week start from here, which is what stops a calendar
 * from disagreeing with the dates printed beside it. Nothing reads the rollout flag: with the flag
 * off the bundle answers en-US, so the pickers follow the rest of the screen either way.
 */
const MuiDateAdapterProvider = ({
  children,
  localeText,
}: {
  children: ReactNode;
  /** Copy for the pickers' own buttons, which belongs to the shell rather than to the adapter. */
  localeText?: LocalizationProviderProps<DateTime, string>['localeText'];
}): JSX.Element => {
  const { calendar, format } = useLocalizationContext();
  // From the calendar rather than from the reader's raw profile, so it follows the rollout flag the
  // way the locale beside it does. The profile does not, and a picker reading it would start the
  // week somewhere `useCalendar().startOfWeek()` does not — the disagreement this file exists to end.
  const { firstDayOfWeek } = calendar;

  return (
    <LocalizationProvider
      dateAdapter={ADAPTERS[firstDayOfWeek]}
      adapterLocale={format.locale()}
      localeText={localeText}
    >
      {children}
    </LocalizationProvider>
  );
};

export default MuiDateAdapterProvider;
